import {
  fetchVistaProjects,
  fetchVistaRubrics,
  fetchVistaAspects,
  createVistaProject,
  updateVistaProject,
  createVistaRubric,
  updateVistaRubric,
  createVistaAspect,
  updateVistaAspect,
} from './vistaJudging';
import { categoryToVistaMapping } from 'constants/evaluation';

export interface ImportResult {
  success: boolean;
  message: string;
  details?: string[];
}

/**
 * Parses a semicolon-separated CSV string into an array of objects.
 * Specifically tailored for the Award Aspect CSV format.
 */
const parseCSV = (csvText: string) => {
  const lines = csvText.split(/\r?\n/).filter(line => line.trim() !== '');
  if (lines.length === 0) return [];

  const headers = lines[0].split(';').map(h => h.trim());
  const rows = [];

  for (let i = 1; i < lines.length; i++) {
    const currentLine = lines[i];
    // Handle quoted fields with semicolons
    const values: string[] = [];
    let start = 0;
    let inQuotes = false;

    for (let j = 0; j < currentLine.length; j++) {
      if (currentLine[j] === '"') {
        inQuotes = !inQuotes;
      } else if (currentLine[j] === ';' && !inQuotes) {
        values.push(
          currentLine
            .substring(start, j)
            .replace(/^"|"$/g, '')
            .replace(/""/g, '"')
        );
        start = j + 1;
      }
    }
    values.push(
      currentLine.substring(start).replace(/^"|"$/g, '').replace(/""/g, '"')
    );

    const row: any = {};
    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });
    rows.push(row);
  }

  return rows;
};

/**
 * Main function to import CSV data into Strapi.
 */
export const importCategoryMappingCSV = async (
  csvText: string,
  year: number
): Promise<ImportResult> => {
  const details: string[] = [];
  try {
    const rows = parseCSV(csvText);
    if (rows.length === 0) {
      return { success: false, message: 'CSV file is empty or invalid.' };
    }

    // 1. Fetch current state from Strapi
    const [projectsRes, rubricsRes, aspectsRes] = await Promise.all([
      fetchVistaProjects(year),
      fetchVistaRubrics(year),
      fetchVistaAspects(year),
    ]);

    const existingProjects = projectsRes.data;
    const existingRubrics = rubricsRes.data;
    const existingAspects = aspectsRes.data;

    // 2. Group rows by Category
    const categoryGroups: Record<string, any[]> = {};
    rows.forEach(row => {
      // Name format: "Category - Aspect"
      const parts = row.name.split(' - ');
      if (parts.length < 2) {
        details.push(`Skipping row with invalid name format: ${row.name}`);
        return;
      }
      const categoryName = parts[0].trim();
      if (!categoryGroups[categoryName]) {
        categoryGroups[categoryName] = [];
      }
      categoryGroups[categoryName].push(row);
    });

    // 3. Process each category
    for (const [categoryName, categoryRows] of Object.entries(categoryGroups)) {
      details.push(`Processing category: ${categoryName}`);

      const vistaProjectName = categoryToVistaMapping(categoryName);

      // Aspect list for the rubric
      const rubricAspectList: { aspect_id: string; weight: number }[] = [];

      for (const row of categoryRows) {
        const parts = row.name.split(' - ');
        const baseAspectName = parts[parts.length - 1].trim();
        const fullAspectName = `${categoryName} - ${baseAspectName}`;
        const weight = parseInt(row.weight.replace('%', '')) || 0;

        // Prepare scale_guidance object
        const scaleGuidance: Record<string, string> = {};
        for (let i = 0; i <= 10; i++) {
          const g = row[`scale_guidance.${i}`];
          if (g) scaleGuidance[String(i)] = g;
        }

        const aspectData = {
          name: fullAspectName,
          description: row.description,
          scale_min: parseInt(row.scale_min) || 0,
          scale_max: parseInt(row.scale_max) || 10,
          scale_guidance: scaleGuidance,
        };

        // Find or Create Aspect
        // Prioritize matching by ID if provided in the CSV
        let aspect = null;
        if (row.id) {
          aspect = existingAspects.find((a: any) => a.documentId === row.id);
        }

        // Fallback to matching by full name if ID not found or not provided
        if (!aspect) {
          aspect = existingAspects.find((a: any) => a.name === fullAspectName);
        }

        let aspectDocumentId = '';

        if (aspect) {
          details.push(
            `  Updating aspect: ${fullAspectName} (ID: ${aspect.documentId})`
          );
          const res = await updateVistaAspect(aspect.documentId, aspectData);
          aspectDocumentId = res.data.documentId;
        } else {
          details.push(`  Creating aspect: ${fullAspectName}`);
          const res = await createVistaAspect(aspectData);
          aspectDocumentId = res.data.documentId;
        }

        rubricAspectList.push({
          aspect_id: aspectDocumentId,
          weight: weight,
        });
      }

      // Find or Create Rubric
      const rubricName = `${categoryName} Rubric`;
      const rubric = existingRubrics.find((r: any) => r.name === rubricName);
      let rubricDocumentId = '';

      const rubricData = {
        name: rubricName,
        aspect_list: rubricAspectList,
      };

      if (rubric) {
        details.push(`  Updating rubric: ${rubricName}`);
        const res = await updateVistaRubric(rubric.documentId, rubricData);
        rubricDocumentId = res.data.documentId;
      } else {
        details.push(`  Creating rubric: ${rubricName}`);
        const res = await createVistaRubric(rubricData);
        rubricDocumentId = res.data.documentId;
      }

      // Find or Create Project
      const project = existingProjects.find(
        (p: any) => p.name === vistaProjectName
      );

      const projectData = {
        name: vistaProjectName,
        rubric_document_id: rubricDocumentId,
        type: 'award_category',
      };

      if (project) {
        details.push(`  Updating project: ${vistaProjectName}`);
        await updateVistaProject(project.documentId, projectData);
      } else {
        details.push(`  Creating project: ${vistaProjectName}`);
        await createVistaProject(projectData);
      }
    }

    return {
      success: true,
      message: 'Import completed successfully.',
      details,
    };
  } catch (error: any) {
    console.error('Import failed:', error);
    return {
      success: false,
      message: `Import failed: ${error.message}`,
      details,
    };
  }
};
